import React from "react";
import NumberFormat from "react-number-format";
import numeral from "numeral";

import { Dropdown, getRange } from "aible";

import { Popover, OverlayTrigger } from "react-bootstrap";

import RangeSlider from "components/Inputs/Slider/RangeSlider";
import RunFrontier from "containers/FrontierSliders/RunFrontier";

import ResponsiveChart from "components/RunPlot/ResponsiveChart";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";

import {
  ScatterChart,
  Scatter,
  LineChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  ReferenceLine,
  ResponsiveContainer,
  Label,
  Cell
} from "recharts";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

import styles from "./style.module.css";

import { getColorFromId } from "utils/misc";

import { GRAPH_TYPES } from "containers/Run/reducer";

const lineColors = ["#512F77", "#4cad61", "#83A6ED", "#7E69D7", "#5BCF75", "#8DD1E1", "#F8CD7E"];

function getMaxChars(labels = []) {
  return labels.filter(isFinite).reduce((acc, curr) => Math.max(curr.toString().length, acc), 0);
}

const CustomLineToolTip = (props) => {
  const { active, payload, label, hasExternalModel } = props;
  if (!active || !payload) {
    return null;
  }
  const currPayload = payload && payload[0] ? payload[0].payload : {};

  payload.sort((p1, p2) => {
    return p2.payload[p2.dataKey] - p1.payload[p1.dataKey];
  });

  return (
    <div className={styles.cTooltip} style={{ fontSize: "14px" }}>
      <div className={styles.tModel}>{currPayload.xLabel + ": " + currPayload.x}</div>
      {payload.map((item) => (
        <div className={styles.tBody} key={item.dataKey}>
          <div className={styles.tLabel} style={{ color: item.stroke }}>
            {(item.payload && item.payload.models && item.payload.models[item.dataKey]
              ? item.payload.models[item.dataKey].label
              : item.dataKey) + ":"}
          </div>
          <div className={styles.tBold} style={{ color: item.stroke }}>
            <NumberFormat
              value={item.payload[item.dataKey]}
              displayType={"text"}
              thousandSeparator={true}
              decimalScale={Math.abs(item.value) > 1000 ? 0 : Math.abs(item.payload[item.dataKey]) < 1 ? undefined : 2}
            />
          </div>
        </div>
      ))}
      {hasExternalModel && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "5px",
            fontSize: "12px"
          }}
        >
          *Dashed lines denote external models
        </div>
      )}
    </div>
  );
};

const CustomToolTip = (props) => {
  const { active, payload, label } = props;
  if (!active || !payload) {
    return null;
  }

  const payloadData = payload.filter((f) => f.name !== "radius");
  const model =
    (payloadData[0] && payloadData[0].payload && payloadData[0].payload.model) ||
    (payloadData[1] && payloadData[1].payload && payloadData[1].payload.model);

  const total =
    (payloadData[0] && payloadData[0].payload && payloadData[0].payload.total) ||
    (payloadData[1] && payloadData[1].payload && payloadData[1].payload.total);

  const probThreshold = model && model.isAdvanced && model.metadata && model.metadata.breakEvenProb;

  const threshold = model && model.maxIteration ? model.maxIteration.threshold : undefined;

  return (
    <div className={styles.cTooltip} style={{ fontSize: "14px" }}>
      <div className={styles.tModel}>{payload[0]?.payload?.model?.label}</div>
      {payloadData.map((item, i) => (
        <div className={styles.tBody} key={i}>
          <div className={styles.tLabel}>{item.name + ":"}</div>
          <div className={styles.tBold}>
            <NumberFormat
              value={item.value}
              displayType={"text"}
              thousandSeparator={true}
              decimalScale={Math.abs(item.value) > 1000 ? 0 : Math.abs(item.value) < 1 ? undefined : 2}
            />
          </div>
        </div>
      ))}
      {total !== undefined && (
        <div className={styles.tBody}>
          <div className={styles.tLabel}>{"Total:"}</div>
          <div className={styles.tBold}>
            <NumberFormat value={total} displayType={"text"} thousandSeparator={true} decimalScale={0} />
          </div>
        </div>
      )}
      {threshold !== undefined && (
        <div className={styles.tBody}>
          <div className={styles.tLabel}>{"Threshold:"}</div>
          <div className={styles.tBold}>{(threshold * 100).toFixed(2) + "%"}</div>
        </div>
      )}
      {probThreshold && (
        <div className={styles.tBody}>
          <div className={styles.tLabel}>{"Probability Threshold (%):"}</div>
          <div className={styles.tBold}>
            <NumberFormat value={probThreshold * 100} displayType={"text"} thousandSeparator={true} decimalScale={2} />
          </div>
        </div>
      )}
    </div>
  );
};

const CustomLabel = (props) => {
  const { active, propsayload, label } = props;

  return (
    <text textAnchor="start" className={styles.aLabel}>
      <tspan
        x={
          props.position === "insideTopLeft"
            ? props.value
              ? 250 / props.value.length
              : 20
            : props.viewBox.x + props.viewBox.width / 2 - 33
        }
        y={props.viewBox.y + props.offset}
      >
        {props.value}
      </tspan>
    </text>
  );
};

const colors = ["red", "green", "pink", "yellow"];

class RunPlot extends React.Component {
  render() {
    const {
      id,
      projectId,
      data,
      hideCosts,
      isQuickRun,
      isSmall,
      hideLegend,
      isOptimizer,
      isMini,
      currentMode,
      models,
      pointerEvents,
      style,
      currentModel,
      hideTitle,
      isContinuous,
      isTwoMinuteRun,
      runPlotCapacity,
      updateRunPlotCapacity,
      runPlotType,
      disableBts,
      selectedMiniBlueprint,
      updateFeatureSelection,
      updateRunPlotType,
      tableContainer,
      hideFrontier
    } = this.props;

    if (data && data.length === 0) {
      return <LoadingIndicator />;
    }

    return (
      <div className={styles.nRunPlot} id="runPlot" style={isSmall || isMini || hideLegend ? { height: "100%" } : {}}>
        {isMini !== true && isOptimizer !== true && <RunPlotButtons {...this.props} />}
        {runPlotType === "SHOW_DETAILS_TABLE" ? (
          <div className={styles.runTable}>{tableContainer}</div>
        ) : runPlotType === GRAPH_TYPES.COST_COMPARISON || runPlotType === GRAPH_TYPES.CAPACITY_COMPARISON ? (
          <RunPlotLine hasSlider={runPlotType === GRAPH_TYPES.CAPACITY_COMPARISON} {...this.props} />
        ) : (
          <RunPlotScatter {...this.props} />
        )}
        {runPlotType === GRAPH_TYPES.CAPACITY_COMPARISON && isMini !== true && isSmall !== true ? (
          <div>
            {!hideFrontier ? (
              <div className={styles.runFrontier}>
                <RunFrontier id={id} projectId={projectId} />
              </div>
            ) : (
              <RunPlotRange {...this.props} />
            )}
          </div>
        ) : (
          ""
        )}
      </div>
    );
  }
}

class RunPlotLine extends React.PureComponent {
  render() {
    const {
      id,
      data,
      isQuickRun,
      isSmall,
      isOptimizer,
      hideLegend,
      isMiniOptimize,
      isMini,
      processLimitPct,
      isHelpClosed,
      isContinuous,
      runPlotType,
      updateFeatureSelection,
      pointerEvents,
      currentModel,
      hasSlider
    } = this.props;

    const xLabel = data && data.length > 0 && data[0].xLabel;
    let xInterval = 0;
    if (data && data.length) {
      if (data.length > 20) {
        xInterval = 4;
        if (data.length > 50) {
          xInterval = 9;
        }
      }
    }
    const yLabel = data && data.length > 0 && data[0].yLabel;
    const modelIds = data && data.length > 0 && data[0].modelIds;
    const hasExternalModel = modelIds.filter((model) => model.includes("external-classifier-")).length > 0;
    let yDataMin = null;
    let yDataMax = null;
    for (const datum of data) {
      for (const datumModel of datum.modelIds) {
        if (yDataMax === null || yDataMin === null) {
          yDataMax = datum[datumModel];
          yDataMin = datum[datumModel];
        } else {
          yDataMin = Math.min(yDataMin, datum[datumModel]);
          yDataMax = Math.max(yDataMax, datum[datumModel]);
        }
      }
    }
    const yDomain = getRange(yDataMin, yDataMax);

    return (
      <div
        className={styles.innerPlot}
        style={{
          height:
            isSmall || hideLegend
              ? "calc(100% - 40px)"
              : isOptimizer
                ? "calc(100% - 60px)"
                : isMiniOptimize
                  ? runPlotType === GRAPH_TYPES.CAPACITY_COMPARISON
                    ? "calc(100% - 100px)"
                    : "calc(100% - 60px)"
                  : isMini
                    ? "100%"
                    : hasSlider
                      ? "calc(100% -  155px)"
                      : "calc(100% -  125px)"
        }}
      >
        <div style={{ height: "100%" }}>
          <ResponsiveContainer>
            <LineChart
              margin={
                isSmall || hideLegend
                  ? { top: 10, right: 10, bottom: 20, left: 10 }
                  : isMini
                    ? { top: 0, right: 0, bottom: 0, left: 0 }
                    : {
                        top: 50,
                        right: 30,
                        bottom: 20,
                        left: 20
                      }
              }
              cursor={isSmall && pointerEvents !== true ? "pointer" : "default"}
              onClick={() => {}}
              data={data}
            >
              <XAxis type="category" dataKey="x" name={xLabel} hide={isSmall || isMini} interval={xInterval}>
                {isSmall !== true && <Label value={xLabel} offset={40} position="bottom" content={<CustomLabel />} />}
              </XAxis>
              <YAxis
                type="number"
                name={yLabel}
                unit={"$"}
                tickFormatter={(tick) => numeral(tick).format("0.[00000]a")}
                hide={isSmall || isMini}
                domain={yDomain}
                interval={0}
              >
                {isSmall !== true && (
                  <Label value={yLabel} offset={-20} position="insideTopLeft" content={<CustomLabel />} />
                )}
              </YAxis>
              <CartesianGrid strokeDasharray="3 3" />
              {isOptimizer && (
                <ReferenceLine
                  x={Math.round(processLimitPct * 100) + "%"}
                  stroke={"#F47C20"}
                  label="Current Capacity"
                />
              )}
              {modelIds &&
                modelIds.map((modelId, i) => {
                  const isExternalModel = modelId.includes("external-classifier-");
                  const width = isExternalModel ? 4 : 2;
                  const dashedLine = isExternalModel ? "5 5" : "";
                  return (
                    <Line
                      key={modelId}
                      dataKey={modelId}
                      stroke={getColorFromId(modelId)}
                      strokeWidth={width}
                      strokeDasharray={dashedLine}
                      dot={false}
                    />
                  );
                })}
              {(isSmall !== true || pointerEvents === true) && isMini !== true && (
                <Tooltip
                  cursor={{
                    strokeDasharray: "4 4",
                    stroke: "var(--aible-color)",
                    strokeWidth: 2
                  }}
                  content={<CustomLineToolTip runPlotType={runPlotType} hasExternalModel={hasExternalModel} />}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
}

class RunPlotScatter extends React.PureComponent {
  render() {
    const {
      id,
      data,
      hideCosts,
      isQuickRun,
      isSmall,
      hideLegend,
      currentMode,
      models,
      pointerEvents,
      style,
      isMiniOptimize,
      isHelpClosed,
      currentModel,
      hideTitle,
      isContinuous,
      isTwoMinuteRun,
      runPlotType,
      disableBts,
      selectedMiniBlueprint,
      updateFeatureSelection,
      updateRunPlotType
    } = this.props;

    const currentState = data && data.filter((m) => m.model.id === "Current State")[0];

    const renderData = data
      .filter((m) => runPlotType !== GRAPH_TYPES.LOG_LOSS || m.model.id !== "Current State")
      .map((d) => ({
        ...d,
        total: runPlotType === GRAPH_TYPES.COUNTS ? d.x + d.y : undefined
      }));

    const xLabel = data && data.length > 0 && data[0].xLabel;
    const yLabel = data && data.length > 0 && data[0].yLabel;

    const yDataMin = Math.min.apply(
      Math,
      renderData.map((m) => m.y || 0)
    );
    const yDataMax = Math.max.apply(
      Math,
      renderData.map((m) => m.y || 0)
    );

    const xDataMin = Math.min.apply(
      Math,
      renderData.map((m) => m.x)
    );
    const xDataMax = Math.max.apply(
      Math,
      renderData.map((m) => m.x)
    );

    let xDomain = getRange(xDataMin, xDataMax);
    const yDomain = getRange(yDataMin, yDataMax);

    if (runPlotType === GRAPH_TYPES.ACCURACY && isContinuous !== true && xLabel === "Accuracy (%)") {
      const low = Math.max(0, xDomain[0]);
      const high = Math.min(100, xDomain[1]);

      xDomain[0] = low;
      xDomain[1] = high;
    }
    if (runPlotType === GRAPH_TYPES.LOG_LOSS) {
      xDomain[0] = Math.min(0, xDomain[0]);
    }
    if (runPlotType === GRAPH_TYPES.COUNTS || runPlotType === GRAPH_TYPES.COSTS) {
      xDomain[0] = Math.max(0, xDomain[0]);
      yDomain[0] = Math.max(0, yDomain[0]);
    }

    const paddingStyle = { padding: "2px 6px" };

    const advModels = renderData.filter((m) => m.model.id.indexOf("-adv") !== -1);
    const otherModels = renderData.filter((m) => m.model.id.indexOf("-adv") === -1);
    const selected = renderData.filter((m) => currentModel && m.model.id === currentModel.id);
    const hasExternalModel = renderData && renderData.filter((m) => m.model.id.includes("external-classifier-"));

    return (
      <div
        className={styles.innerPlot}
        style={{
          height:
            isSmall || hideLegend
              ? "calc(100% - 40px)"
              : runPlotType === GRAPH_TYPES.CAPACITY_COMPARISON
                ? "calc(100% - 100px)"
                : "calc(100% - 60px)"
        }}
      >
        <div style={{ height: isSmall || isMiniOptimize || hideLegend ? "100%" : "calc(100% - 60px)" }}>
          <ResponsiveContainer>
            <ScatterChart
              margin={
                isSmall
                  ? { top: 10, right: 10, bottom: 20, left: 10 }
                  : {
                      top: 50,
                      right: 30,
                      bottom: 20,
                      left: 20
                    }
              }
              cursor={isSmall && pointerEvents !== true ? "pointer" : "default"}
              onClick={() => {}}
            >
              <XAxis
                type="number"
                dataKey="x"
                name={xLabel}
                unit={xLabel === "Accuracy (%)" ? "%" : ""}
                tickFormatter={(tick) => numeral(tick).format("0.[00000]a")}
                domain={xDomain}
                hide={isSmall}
              >
                {isSmall !== true && <Label value={xLabel} offset={40} position="bottom" content={<CustomLabel />} />}
              </XAxis>
              <YAxis
                type="number"
                dataKey="y"
                name={yLabel}
                unit={xLabel === "Accuracy (%)" ? "$" : ""}
                tickFormatter={(tick) => numeral(tick).format("0.[00000]a")}
                domain={yDomain}
                hide={isSmall}
                interval={0}
              >
                {isSmall !== true && (
                  <Label value={yLabel} offset={-20} position="insideTopLeft" content={<CustomLabel />} />
                )}
              </YAxis>
              <ZAxis dataKey="z" type="number" name="radius" range={[100, 200]} />
              <CartesianGrid strokeDasharray="3 3" />
              <Scatter
                name="Models"
                data={otherModels.map((m, index) => ({
                  ...m,
                  y: m.y ? m.y.toFixed(2) : 0,
                  x: m.x ? m.x.toFixed(2) : 0,
                  z: currentModel && m.model.id === currentModel.id ? 100 : 1
                }))}
                fill="#ff7300"
                animationDuration={0}
              >
                {otherModels.map((entry, index) => {
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.model.id.includes("external-classifier-")
                          ? "#EF7E54"
                          : currentModel && entry.model.id === currentModel.id
                            ? "transparent"
                            : entry.model.model_metadata.aible_model
                              ? "transparent"
                              : entry.model.id === "Current State"
                                ? "#C8CAD4"
                                : currentState !== undefined
                                  ? entry.y > currentState.y
                                    ? "rgb(146, 207, 72)"
                                    : "rgb(75, 166, 186)"
                                  : "#C8CAD4"
                      }
                      stroke={
                        entry.model.id.includes("external-classifier-")
                          ? "var(--aible-color)"
                          : currentModel && entry.model.id === currentModel.id
                            ? "transparent"
                            : entry.model.model_metadata.aible_model
                              ? "var(--aible-color)"
                              : "transparent"
                      }
                      strokeWidth={2}
                      onClick={(e) => entry && entry.model && updateFeatureSelection(entry.model.id)}
                    />
                  );
                })}
              </Scatter>
              <Scatter
                name="AdvModels"
                data={advModels.map((m, index) => ({
                  ...m,
                  y: m.y ? m.y.toFixed(2) : 0,
                  x: m.x ? m.x.toFixed(2) : 0,
                  z: currentModel && m.model.id === currentModel.id ? 100 : 1
                }))}
                shape={"triangle"}
                animationDuration={0}
              >
                {advModels.map((entry, index) => {
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={currentModel && currentModel.id === entry.model.id ? "transparent" : "transparent"}
                      stroke={currentModel && currentModel.id === entry.model.id ? "transparent" : "var(--aible-color)"}
                      strokeWidth={2}
                      onClick={(e) => entry && entry.model && updateFeatureSelection(entry.model.id)}
                    />
                  );
                })}
              </Scatter>
              <Scatter
                name="Selected"
                data={selected.map((m, index) => ({
                  ...m,
                  y: m.y ? m.y.toFixed(2) : 0,
                  x: m.x ? m.x.toFixed(2) : 0,
                  z: 1000
                }))}
                shape={selected && selected[0] && selected[0].model.id.indexOf("-adv") === -1 ? "circle" : "triangle"}
                animationDuration={0}
              >
                {selected.map((entry, index) => {
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={"#F47C20"}
                      stroke={"#F47C20"}
                      strokeWidth={2}
                      onClick={(e) => entry && entry.model && updateFeatureSelection(entry.model.id)}
                    />
                  );
                })}
              </Scatter>
              {currentState && runPlotType !== GRAPH_TYPES.LOG_LOSS && (
                <ReferenceLine x={currentState.x} stroke="#F47C20" />
              )}
              {currentState && (
                <ReferenceLine
                  y={currentState.y}
                  stroke="#F47C20"
                  label={runPlotType !== GRAPH_TYPES.LOG_LOSS ? "" : "Current State Impact"}
                />
              )}
              {(isSmall !== true || pointerEvents === true) && (
                <Tooltip
                  cursor={{
                    strokeDasharray: "4 4",
                    stroke: "var(--aible-color)",
                    strokeWidth: 2
                  }}
                  content={<CustomToolTip runPlotType={runPlotType} />}
                />
              )}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        {isSmall !== true && hideLegend !== true && (
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <div className={styles.legendIconAible} />
              <div className={styles.legendName}>{"Aible Models"}</div>
            </div>
            {/* Legend for Advanced Models */}
            {/* <div className={styles.legendItem}>
              <div className={styles.legendIconStandardOptimized}>
                <div className={styles.innerTraingle} />
              </div>
              <div className={styles.legendName}>{"Standard Model Aible Optimized"}</div>
            </div> */}
            <div className={styles.legendItem}>
              <div className={styles.legendIconStandard} />
              <div className={styles.legendName}>{"Standard Models"}</div>
            </div>
            <div className={styles.legendItem}>
              <div className={styles.legendIconSelected} />
              <div className={styles.legendName}>{"Selected Model"}</div>
            </div>
            {hasExternalModel && hasExternalModel.length > 0 && (
              <div className={styles.legendItem}>
                <div className={styles.legendIconExternalModel} />
                <div className={styles.legendName}>{"External Model"}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

class RunPlotButtons extends React.PureComponent {
  render() {
    const {
      id,
      data,
      hideCosts,
      isQuickRun,
      isSmall,
      currentMode,
      models,
      pointerEvents,
      style,
      currentModel,
      hideTitle,
      isContinuous,
      isTwoMinuteRun,
      runPlotType,
      disableBts,
      selectedMiniBlueprint,
      updateFeatureSelection,
      updateRunPlotType,
      forceEnableBts,
      hideLineGraphs,
      tableContainer,
      tableToggle
    } = this.props;

    const GRAPH_EXPLANATIONS = {
      [GRAPH_TYPES.COUNTS]: "This chart compares the false positive and false negative performance for each model.",
      [GRAPH_TYPES.COSTS]:
        "This chart scales the raw counts chart to the cost ratio that you have selected." +
        (selectedMiniBlueprint !== undefined
          ? " In this case you selected a " +
            selectedMiniBlueprint.truePositive +
            ":" +
            Math.abs(selectedMiniBlueprint.falsePositive) +
            " cost ratio so the false negatives are scaled " +
            Math.abs(selectedMiniBlueprint.falsePositive) +
            " times more. "
          : ""),
      [GRAPH_TYPES.ACCURACY]: "Relative impact compared against Accuracy (%)",
      [GRAPH_TYPES.LOG_LOSS]: "Impact compared against Log Loss",
      [GRAPH_TYPES.COST_COMPARISON]: "Compares different model impacts at increasing cost ratios",
      [GRAPH_TYPES.ERROR_VS_AVERAGE]:
        "This chart compares the total error cost with the raw average error for each model."
    };

    return (
      <div className={styles.buttonWrapper}>
        {isSmall ? (
          <div className={styles.smallBtns}>
            <Dropdown
              className={styles.smallBtn}
              selected={runPlotType}
              options={[
                {
                  label: isContinuous ? "Over / Under" : "Raw Counts",
                  value: GRAPH_TYPES.COUNTS,
                  className: styles.smallBtn
                },
                {
                  label: "Cost Comparison",
                  value: GRAPH_TYPES.COST_COMPARISON,
                  className: styles.smallBtn,
                  hide: isContinuous || hideLineGraphs || !isQuickRun
                },
                {
                  label: "Cost Ratio",
                  value: GRAPH_TYPES.COSTS,
                  className: styles.smallBtn,
                  disabled: !forceEnableBts && isQuickRun && disableBts,
                  hide: !isContinuous
                },
                {
                  label: "Capacity Comparison",
                  value: GRAPH_TYPES.CAPACITY_COMPARISON,
                  className: styles.smallBtn,
                  hide: isContinuous,
                  disabled: !forceEnableBts && disableBts
                },
                {
                  label: "Total Error Cost vs Average Prediction Error",
                  value: GRAPH_TYPES.ERROR_VS_AVERAGE,
                  className: styles.smallBtn,
                  hide: !isContinuous,
                  disabled: !forceEnableBts && isQuickRun && disableBts
                },
                {
                  label: "Impact vs Accuracy",
                  value: GRAPH_TYPES.ACCURACY,
                  className: styles.smallBtn,
                  hide: isContinuous,
                  disabled: !forceEnableBts && isQuickRun && disableBts
                },
                {
                  label: "Impact vs Processed",
                  value: GRAPH_TYPES.PROCESSED,
                  className: styles.smallBtn,
                  hide: isContinuous,
                  disabled: !forceEnableBts && isQuickRun && disableBts
                },
                {
                  label: "Impact vs Log Loss",
                  value: GRAPH_TYPES.LOG_LOSS,
                  className: styles.smallBtn,
                  hide: isContinuous,
                  disabled: !forceEnableBts && isQuickRun && disableBts
                }
              ]}
              onChange={(option) => updateRunPlotType(option.value)}
            />
          </div>
        ) : (
          <div className={styles.tButtons}>
            <Dropdown
              selected={runPlotType}
              options={[
                ...(tableContainer
                  ? [
                      // {
                      //   label: "List View",
                      //   value: "SHOW_DETAILS_TABLE",
                      // },
                    ]
                  : []),
                {
                  label: isContinuous ? "Over / Under" : "Raw Counts",
                  value: GRAPH_TYPES.COUNTS
                },
                {
                  label: "Cost Comparison",
                  value: GRAPH_TYPES.COST_COMPARISON,
                  hide: isContinuous || hideLineGraphs || !isQuickRun
                },
                {
                  label: "Cost Ratio",
                  value: GRAPH_TYPES.COSTS,
                  disabled: !forceEnableBts && isQuickRun && selectedMiniBlueprint === undefined,
                  hide: !isContinuous
                },
                {
                  label: "Total Error Cost vs Average Prediction Error",
                  value: GRAPH_TYPES.ERROR_VS_AVERAGE,
                  hide: !isContinuous,
                  disabled: !forceEnableBts && isQuickRun && selectedMiniBlueprint === undefined
                },
                {
                  label: "Capacity Comparison",
                  value: GRAPH_TYPES.CAPACITY_COMPARISON,
                  hide: isContinuous,
                  disabled: !forceEnableBts && isQuickRun && selectedMiniBlueprint === undefined
                },
                {
                  label: "Impact vs Accuracy",
                  value: GRAPH_TYPES.ACCURACY,
                  hide: isContinuous,
                  disabled: !forceEnableBts && isQuickRun && selectedMiniBlueprint === undefined
                },
                {
                  label: "Impact vs Processed",
                  value: GRAPH_TYPES.PROCESSED,
                  hide: isContinuous,
                  disabled: !forceEnableBts && isQuickRun && selectedMiniBlueprint === undefined
                },
                {
                  label: "Impact vs Log Loss",
                  value: GRAPH_TYPES.LOG_LOSS,
                  hide: isContinuous,
                  disabled: !forceEnableBts && isQuickRun && selectedMiniBlueprint === undefined
                }
              ]}
              onChange={(option) => updateRunPlotType(option.value)}
            />
            {tableToggle ? (
              //&& runPlotType === "SHOW_DETAILS_TABLE"
              <div className={styles.tableButtons} style={{ marginTop: "0px", marginLeft: "5px" }}>
                {tableToggle}
              </div>
            ) : (
              ""
            )}
            {GRAPH_EXPLANATIONS[runPlotType] ? (
              <div className={styles.pHolder}>
                <OverlayTrigger
                  placement="top"
                  trigger={["hover", "click"]}
                  rootClose
                  overlay={
                    <Popover id="popover-positioned-top" title={runPlotType}>
                      {GRAPH_EXPLANATIONS[runPlotType]}
                    </Popover>
                  }
                >
                  <FontAwesomeIcon icon={faInfo} className={styles.typeIcon} />
                </OverlayTrigger>
              </div>
            ) : (
              ""
            )}
          </div>
        )}
      </div>
    );
  }
}

class RunPlotRange extends React.PureComponent {
  render() {
    const { runPlotCapacity, updateRunPlotCapacity, isMiniOptimize } = this.props;

    return (
      <div className={styles.rangeWrapper}>
        <RangeSlider
          min={0}
          max={100}
          value={runPlotCapacity}
          onChange={(val) => updateRunPlotCapacity(val)}
          startLabel={"Min. %"}
          endLabel={"Max. %"}
          inputSliderClassName={styles.rangeSliderWrapper}
          isMiniOptimize={isMiniOptimize}
        />
      </div>
    );
  }
}

export default ResponsiveChart(RunPlot);
